// src/components/resumo/PopoverManager.ts
// ✅ CORREÇÃO: Remover emoji do hint, manter funcionalidade 100%

export class PopoverManager {
    private editorRef: React.RefObject<HTMLDivElement | null>;
    private currentPopover: HTMLElement | null = null;
    private popoverTimeout: ReturnType<typeof setTimeout> | null = null;
    private isDragging = false;
    private cards: any[] = [];
    private darkMode: boolean = false;
    private domObserver: MutationObserver | null = null;
    private handleDOMChange: (() => void) | null = null;

    constructor(editorRef: React.RefObject<HTMLDivElement | null>, cards: any[], darkMode: boolean = false) {
        this.editorRef = editorRef;
        this.cards = cards;
        this.darkMode = darkMode;
    }

    updateData(cards: any[], darkMode: boolean) {
        this.cards = cards;
        this.darkMode = darkMode;
    }

    initialize() {
        console.log('🔄 Inicializando PopoverManager...');

        const editor = this.editorRef.current;
        if (!editor) {
            console.error('❌ Editor não encontrado no PopoverManager');
            return;
        }

        document.addEventListener('mouseover', this.handleMouseOver);
        document.addEventListener('dragstart', this.handleDragStart);
        document.addEventListener('dragend', this.handleDragEnd);
        document.addEventListener('mousedown', this.handleMouseDown);
        document.addEventListener('mouseup', this.handleMouseUp);
        document.addEventListener('badge:toggleAnswer', this.handleToggleAnswer as EventListener);
        document.addEventListener('click', this.handleOutsideClick);

        this.handleDOMChange = () => {
            const popover = document.querySelector('.flashcard-popover');
            if (popover) {
                const badgeId = (popover as any)._badgeId;
                if (badgeId) {
                    const badge = document.querySelector(`[data-card-id="${badgeId}"]`);
                    if (!badge) {
                        console.log('🗑️ Badge deletado, removendo popover');
                        this.removePopover();
                    }
                }
            }
        };

        const observer = new MutationObserver(this.handleDOMChange);
        observer.observe(editor, {
            childList: true,
            subtree: true
        });

        this.domObserver = observer;

        console.log('✅ PopoverManager inicializado');
    }

    private handleDragStart = () => {
        this.isDragging = true;
        this.removePopover();
    };

    private handleDragEnd = () => {
        setTimeout(() => {
            this.isDragging = false;
        }, 100);
    };

    private handleMouseDown = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        if (target.closest('.badge-eye-btn')) {
            return;
        }

        if (target.closest('.flashcard-badge[data-editable="true"]')) {
            this.isDragging = true;
            this.removePopover();
        }
    };

    private handleMouseUp = () => {
        setTimeout(() => {
            this.isDragging = false;
        }, 100);
    };

    private handleOutsideClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        if (target.closest('.badge-eye-btn')) {
            return;
        }

        if (
            this.currentPopover &&
            !this.currentPopover.contains(target) &&
            !target.closest('.flashcard-badge')
        ) {
            this.removePopover();
        }
    };

    private handleToggleAnswer = (e: Event) => {
        const customEvent = e as CustomEvent;
        const { badge, card, cardIndex, originalEvent } = customEvent.detail;

        if (originalEvent instanceof MouseEvent) {
            originalEvent.preventDefault();
            originalEvent.stopPropagation();
        }

        if (!this.currentPopover) {
            this.createPopover(badge, card, cardIndex);
        }

        const popover = this.currentPopover;
        if (!popover) return;

        const currentShow = popover.dataset.showAnswer === 'true';
        const newShow = !currentShow;
        popover.dataset.showAnswer = String(newShow);

        console.log(
            `🔄 Alternando: ${currentShow ? 'RESPOSTA' : 'PERGUNTA'} → ${newShow ? 'RESPOSTA' : 'PERGUNTA'}`
        );

        const textEl = popover.querySelector('.flashcard-badge-popover-text') as HTMLElement | null;
        const labelEl = popover.querySelector('.flashcard-badge-popover-label') as HTMLElement | null;

        if (!textEl || !labelEl) return;

        labelEl.textContent = newShow ? 'RESPOSTA' : 'PERGUNTA';

        textEl.classList.add('fade-out');

        const cleanHTML = (html: string): string => {
            const temp = document.createElement('div');
            temp.innerHTML = html;
            temp.querySelectorAll('button').forEach(btn => btn.remove());
            temp.querySelectorAll('[class*="remove"]').forEach(el => el.remove());
            temp.querySelectorAll('[class*="edit"]').forEach(el => el.remove());
            return temp.innerHTML;
        };

        setTimeout(() => {
            textEl.innerHTML = newShow
                ? cleanHTML(card.back || '')
                : cleanHTML(card.front || '');
            textEl.classList.remove('fade-out');
        }, 150);

    };

    private getRenderFunction(card: any) {
        return (showAnswer: boolean) => {
            const cleanHTML = (html: string): string => {
                const temp = document.createElement('div');
                temp.innerHTML = html;
                temp.querySelectorAll('button').forEach(btn => btn.remove());
                return temp.innerHTML;
            };

            const frontClean = cleanHTML(card.front || '');
            const backClean = cleanHTML(card.back || '');

            // ✅ ÚNICO AJUSTE: Remover emoji do hint
            return `
            <div class="flashcard-badge-popover-content">
                <div class="flashcard-badge-popover-label">
                    ${showAnswer ? 'RESPOSTA' : 'PERGUNTA'}
                </div>
                <div class="flashcard-badge-popover-text">
                    ${showAnswer ? backClean : frontClean}
                </div>
                ${card.image && showAnswer ? `
                    <div class="flashcard-badge-popover-image">
                        <img src="${card.image}" alt="Imagem" style="width: 100%; border-radius: 8px; margin-top: 12px;" />
                    </div>
                ` : ''}
                <div class="flashcard-badge-popover-hint">
                    Clique no ícone do olho para ${showAnswer ? 'voltar' : 'ver a resposta'}
                </div>
            </div>
        `;
        };
    }

    private handleMouseOver = (e: MouseEvent) => {
        if (this.isDragging) return;

        const target = e.target as HTMLElement;

        if (target.classList.contains('flashcard-badge') || target.closest('.flashcard-badge')) {
            const badge = target.classList.contains('flashcard-badge')
                ? target
                : target.closest('.flashcard-badge') as HTMLElement;

            if (!badge) return;

            const cardIndex = parseInt(badge.dataset.cardIndex || '0');

            if (!this.cards || !this.cards[cardIndex]) return;

            const card = this.cards[cardIndex];

            this.popoverTimeout = setTimeout(() => {
                if (this.isDragging) return;

                this.removePopover();
                this.createPopover(badge, card, cardIndex);

                console.log('✅ Popover exibido após 1.5s de hover');
            }, 1500);
        }
    };

    private createPopover(badge: HTMLElement, card: any, cardIndex: number) {
        const popover = document.createElement('div');
        popover.className = `flashcard-popover ${this.darkMode ? 'dark' : ''}`;
        popover.dataset.showAnswer = 'false';
        popover.style.pointerEvents = 'auto';

        (popover as any)._badgeId = badge.dataset.cardId;

        this.currentPopover = popover;

        const renderPopoverContent = (showAnswer: boolean) => {
            const cleanHTML = (html: string): string => {
                const temp = document.createElement('div');
                temp.innerHTML = html;
                temp.querySelectorAll('button').forEach(btn => btn.remove());
                temp.querySelectorAll('[class*="remove"]').forEach(el => el.remove());
                temp.querySelectorAll('[class*="edit"]').forEach(el => el.remove());
                return temp.innerHTML;
            };

            const frontClean = cleanHTML(card.front || '');
            const backClean = cleanHTML(card.back || '');

            // ✅ ÚNICO AJUSTE: Remover emoji do hint
            return `
                <div class="flashcard-badge-popover-content">
                    <div class="flashcard-badge-popover-label">
                        ${showAnswer ? 'RESPOSTA' : 'PERGUNTA'}
                    </div>
                    <div class="flashcard-badge-popover-text">
                        ${showAnswer ? backClean : frontClean}
                    </div>
                    ${card.image && showAnswer ? `
                        <div class="flashcard-badge-popover-image">
                            <img src="${card.image}" alt="Imagem" style="width: 100%; border-radius: 8px; margin-top: 12px;" />
                        </div>
                    ` : ''}
                    <div class="flashcard-badge-popover-hint">
                        Clique no ícone do olho para ${showAnswer ? 'voltar' : 'ver a resposta'}
                    </div>
                </div>
            `;
        };

        popover.innerHTML = renderPopoverContent(false);

        const rect = badge.getBoundingClientRect();
        popover.style.position = 'fixed';
        popover.style.left = `${rect.left}px`;
        popover.style.top = `${rect.bottom + 8}px`;
        popover.style.transform = 'none';

        document.body.appendChild(popover);

        const popoverRect = popover.getBoundingClientRect();

        if (popoverRect.right > window.innerWidth) {
            popover.style.left = `${window.innerWidth - popoverRect.width - 16}px`;
        }

        if (popoverRect.bottom > window.innerHeight) {
            const availableHeight = window.innerHeight - rect.bottom - 24;

            if (availableHeight < 100) {
                popover.style.top = `${rect.top - popoverRect.height - 8}px`;
            } else {
                popover.style.maxHeight = `${availableHeight}px`;
                popover.style.overflowY = 'auto';
            }
        }

        let isOverPopover = false;

        const removePopoverHandler = (e: MouseEvent) => {
            const target = e.relatedTarget as HTMLElement | null;

            if (target && target.closest('.badge-eye-btn')) {
                return;
            }

            if (target && target.closest('.flashcard-popover')) {
                return;
            }

            if (!target) {
                return;
            }

            setTimeout(() => {
                if (!isOverPopover) {
                    this.removePopover();
                }
            }, 100);
        };

        popover.addEventListener('mouseenter', () => {
            isOverPopover = true;
        });

        popover.addEventListener('mouseleave', (e) => {
            const target = e.relatedTarget as HTMLElement | null;

            if (target && target.closest('.badge-eye-btn')) {
                return;
            }

            isOverPopover = false;
        });

        badge.addEventListener('mouseleave', removePopoverHandler);

        (badge as any)._popoverRemoveHandler = removePopoverHandler;

        console.log('✅ Popover criado:', {
            cardIndex,
            badgeId: badge.dataset.cardId,
            position: { left: popover.style.left, top: popover.style.top }
        });
    }

    removePopover() {
        if (this.popoverTimeout) {
            clearTimeout(this.popoverTimeout);
            this.popoverTimeout = null;
        }

        if (this.currentPopover) {
            this.currentPopover.remove();
            this.currentPopover = null;
            console.log('🗑️ Popover removido');
        }

        const badges = document.querySelectorAll('.flashcard-badge');
        badges.forEach(badge => {
            const handler = (badge as any)._popoverRemoveHandler;
            if (handler) {
                badge.removeEventListener('mouseleave', handler);
                delete (badge as any)._popoverRemoveHandler;
            }
        });
    }

    cleanup() {
        console.log('🧹 Limpando PopoverManager...');

        if (this.domObserver) {
            this.domObserver.disconnect();
            this.domObserver = null;
        }

        this.removePopover();

        document.removeEventListener('mouseover', this.handleMouseOver);
        document.removeEventListener('dragstart', this.handleDragStart);
        document.removeEventListener('dragend', this.handleDragEnd);
        document.removeEventListener('mousedown', this.handleMouseDown);
        document.removeEventListener('mouseup', this.handleMouseUp);
        document.removeEventListener('badge:toggleAnswer', this.handleToggleAnswer as EventListener);
        document.removeEventListener('click', this.handleOutsideClick);

        const badges = document.querySelectorAll('.flashcard-badge');
        badges.forEach(badge => {
            const eyeBtn = badge.querySelector('.badge-eye-btn');
            if (eyeBtn) {
                const handler = (eyeBtn as any)._popoverClickHandler;
                if (handler) {
                    eyeBtn.removeEventListener('click', handler);
                    delete (eyeBtn as any)._popoverClickHandler;
                }
            }

            const removeHandler = (badge as any)._popoverRemoveHandler;
            if (removeHandler) {
                badge.removeEventListener('mouseleave', removeHandler);
                delete (badge as any)._popoverRemoveHandler;
            }
        });

        console.log('✅ PopoverManager cleanup concluído');
    }
}